import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { settlementsApi, EligibleOrderEntry, RunCycleResult, runCycleReasonText } from '../../services/settlementsApi';
import { useAuth } from '../../hooks/useAuth';

interface RunSettlementCardProps {
  garageId: string;
}

const aed = (n: number | null) =>
  n == null ? '-' : `AED ${n.toLocaleString('en-AE', { minimumFractionDigits: 2 })}`;

const dateOf = (iso: string | null) => (iso ? new Date(iso).toLocaleDateString() : '-');

/** A row a manual run would wrap right now: hold expired (or expiring as the run promotes it). */
const isCollectable = (o: EligibleOrderEntry) =>
  (o.settlementState === 'ELIGIBLE' || o.settlementState === 'AWAITING_HOLD') &&
  o.settlementEligibleAt != null && new Date(o.settlementEligibleAt).getTime() <= Date.now();

const stateLabel = (o: EligibleOrderEntry) => {
  if (isCollectable(o)) return { text: 'Ready', cls: 'approved' };
  if (o.settlementState === 'AWAITING_HOLD') return { text: 'In hold window', cls: 'pending' };
  if (o.settlementState === 'ON_HOLD_DISPUTE') return { text: 'Disputed', cls: 'warning' };
  if (o.settlementState === 'FULLY_REFUNDED') return { text: 'Fully refunded', cls: 'rejected' };
  return { text: o.settlementState, cls: 'pending' };
};

/**
 * Manual settlement (garage settlement tab). Previews the garage's unsettled orders, then calls
 * POST /api/admin/settlements/run-cycle — which wraps every collectable order since the garage's
 * last settlement plus open adjustments. Orders inside the dispute-hold window stay behind for a
 * later run.
 */
export default function RunSettlementCard({ garageId }: RunSettlementCardProps) {
  const auth = useAuth();
  const [orders, setOrders] = useState<EligibleOrderEntry[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [confirming, setConfirming] = useState(false);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<RunCycleResult | null>(null);

  const fetchOrders = useCallback(async () => {
    if (!auth.token) return;
    setLoading(true);
    try {
      const res = await settlementsApi.getEligibleOrders(garageId, auth.token);
      setOrders(res.orders);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load unsettled orders');
    } finally {
      setLoading(false);
    }
  }, [auth.token, garageId]);

  useEffect(() => { fetchOrders(); }, [fetchOrders]);

  const handleRun = async () => {
    if (!auth.token) return;
    setRunning(true);
    setError(null);
    setResult(null);
    try {
      const res = await settlementsApi.runCycle(garageId, auth.token);
      setResult(res);
      if (res.generated) fetchOrders();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to run settlement');
    } finally {
      setRunning(false);
      setConfirming(false);
    }
  };

  const collectable = (orders ?? []).filter(isCollectable);
  const collectableGross = collectable.reduce((sum, o) => sum + (o.amount ?? 0), 0);

  return (
    <div className="admin-card" style={{ marginTop: '1rem' }}>
      <h3 className="admin-card-title" style={{ marginBottom: '0.5rem' }}>Manual Settlement</h3>
      <p style={{ fontSize: '0.875rem', color: 'var(--admin-text-muted)', margin: '0 0 1rem' }}>
        Unsettled completed orders for this garage. Running a settlement now wraps every order
        marked <strong>Ready</strong> (all completed orders since the last settlement whose hold
        window has passed) plus any open adjustments.
      </p>

      {error && <div className="error-message" style={{ marginBottom: '0.75rem' }}>{error}</div>}

      {result && (
        <div
          role="status"
          data-testid="run-cycle-result"
          style={{
            background: result.generated ? '#f0fdf4' : '#fffbeb',
            border: `1px solid ${result.generated ? '#bbf7d0' : '#fde68a'}`,
            color: result.generated ? '#166534' : '#92400e',
            padding: '0.75rem 1rem', borderRadius: 6, marginBottom: '0.75rem', fontSize: '0.875rem',
          }}
        >
          {result.generated ? (
            <>
              Settlement created.{' '}
              <Link to={`/settlements/${result.settlementId}`} className="admin-link">Open settlement</Link>
            </>
          ) : (
            // #520 — the old copy named both no-ops at once ("…, or a settlement for this period
            // already exists"), so it never told the admin which one had happened.
            runCycleReasonText(result)
          )}
        </div>
      )}

      {loading ? (
        <div className="admin-loading"><div className="admin-loading-spinner" /></div>
      ) : !orders || orders.length === 0 ? (
        <div className="admin-empty-state"><p>No unsettled orders for this garage.</p></div>
      ) : (
        <div className="admin-table-wrapper" style={{ marginBottom: '1rem' }}>
          <table className="admin-table">
            <thead>
              <tr>
                <th>Order</th><th>Customer</th><th>Service</th><th>Completed</th>
                <th style={{ textAlign: 'right' }}>Amount</th>
                <th style={{ textAlign: 'right' }}>Net</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {orders.map((o) => {
                const label = stateLabel(o);
                const ready = isCollectable(o);
                return (
                  <tr key={o.orderId} style={ready ? undefined : { opacity: 0.55 }}>
                    <td style={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>{o.orderId.slice(0, 8)}</td>
                    <td>{o.customerName || '-'}</td>
                    <td>{o.serviceSummary || '-'}</td>
                    <td>{dateOf(o.completedAt)}</td>
                    <td style={{ textAlign: 'right' }}>{aed(o.amount)}</td>
                    <td style={{ textAlign: 'right' }}>{aed(o.netAmount)}</td>
                    <td><span className={`admin-badge ${label.cls}`}>{label.text}</span></td>
                  </tr>
                );
              })}
            </tbody>
            {collectable.length > 0 && (
              <tfoot>
                <tr>
                  <td colSpan={4} style={{ fontWeight: 600 }}>
                    Ready to settle: {collectable.length} order{collectable.length === 1 ? '' : 's'}
                  </td>
                  <td style={{ textAlign: 'right', fontWeight: 600 }}>{aed(collectableGross)}</td>
                  <td colSpan={2}></td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      )}

      {!confirming ? (
        <button
          type="button"
          className="admin-btn admin-btn-outline admin-btn-sm"
          onClick={() => { setConfirming(true); setResult(null); setError(null); }}
        >
          Run settlement now
        </button>
      ) : (
        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
          <span style={{ fontSize: '0.875rem', color: 'var(--admin-text)' }}>
            {collectable.length > 0
              ? `Create a settlement wrapping ${collectable.length} ready order${collectable.length === 1 ? '' : 's'} (${aed(collectableGross)} gross)?`
              : 'No ready orders — a settlement will only be created if open adjustments exist. Continue?'}
          </span>
          <button type="button" className="admin-btn admin-btn-primary admin-btn-sm" onClick={handleRun} disabled={running}>
            {running ? 'Running…' : 'Confirm'}
          </button>
          <button type="button" className="admin-btn admin-btn-outline admin-btn-sm" onClick={() => setConfirming(false)} disabled={running}>
            Cancel
          </button>
        </div>
      )}
    </div>
  );
}
